import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import MetaTags from 'components/common/MetaTags';
import PageHeader from 'components/NewDesign/PageHeader';
import MainContent from 'components/NewDesign/MainContent';
import LoadingContent from 'components/NewDesign/LoadingContent';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import HousePoints from 'components/admin/reports/HousePoints';
import styles from 'components/admin/reports/HousePoints/HousePoints.module.css';
import { getHousePoints, exportHousePointsToFile } from 'api/reportsApi';
import { exportAndDownloadFile } from 'utils/fileDownload';
import { seasonHasHousePoints } from 'utils/meetPoints';
import { teamWordsOf } from 'utils/teamLabel';

// Reports, House Points (points slice 5, docs/school-carnivals-points-slice-5.md in the portal repo): a
// school's houses over the active season, a column per meet that counts for season points, released to
// members or not (Doug, 2026-10-26: "It's an admin report. Admins should see all the points here."). Named
// in the school's word: House Points, Faction Points or Team Points. The page asks the server once when it
// opens; the reply carries the season's name for the subtitle, so the page makes no other request.
const HousePointsPage = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const clubId = selectedClub?.clubID;
  const teamWords = teamWordsOf(selectedClub);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId) return undefined;
    // A reply for a club switched away from is dropped.
    let cancelled = false;
    setLoading(true);
    getHousePoints(clubId)
      .then((response) => {
        if (!cancelled) setReport(response.data);
      })
      .catch((err) => {
        console.error('[HousePoints] Fetch house points error: ', err);
        if (!cancelled) {
          setReport(null);
          toast.error(`Failed to load the ${teamWords.lower.team} points`);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId, teamWords.lower.team]);

  const hasPoints = seasonHasHousePoints(report);

  const handleExportToFile = async () => {
    await exportAndDownloadFile(
      exportHousePointsToFile,
      [clubId],
      '',
      `The ${teamWords.lower.team} points were successfully exported`,
      `Fetching the ${teamWords.lower.team} points from the server...`
    );
  };

  return (
    <>
      <MetaTags title={`SplitFast | ${teamWords.team} Points Report`} />
      <MainContent>
        <PageHeader title={`${teamWords.team} Points`} subTitle={report?.seasonName || ''} primarySubTitle>
          <CustomNewButton
            variant="info"
            onClick={handleExportToFile}
            label="Export to file"
            disabled={loading || !hasPoints}
            tooltip={`Download the season's ${teamWords.lower.team} points as a CSV file.`}
          />
        </PageHeader>
        {!clubId ? (
          <div style={{ minHeight: '100vh' }} />
        ) : (
          <LoadingContent loading={loading}>
            {/* Nothing but the error message when the report could not be loaded. */}
            {hasPoints ? (
              <HousePoints clubId={clubId} meets={report.meets} houses={report.houses} teamWords={teamWords} />
            ) : (
              report !== null && (
                <div className={styles.card}>
                  <div className={styles.empty}>No {teamWords.lower.team} points this season yet.</div>
                </div>
              )
            )}
          </LoadingContent>
        )}
      </MainContent>
    </>
  );
};

export default HousePointsPage;
